import { useEffect, useState } from 'react';
import { Icon } from '@/components/Icon';
import { Spinner } from '@/components/Spinner';
import { Pager } from '@/components/Pager';
import { SearchBox } from '@/components/form/SearchBox';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import { useAsyncData } from '@/hooks/useAsyncData';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import { useTooltip } from '@/hooks/useTooltip';
import { showToast } from '@/lib/toast';
import {
  fetchOperatorTemplates,
  createOperatorTemplate,
  updateOperatorTemplate,
  deleteOperatorTemplate,
  type OperatorTemplateRow,
} from '@/api/operatorTemplates';
import { OperatorTemplateForm } from './OperatorTemplateForm';
import { OperatorTemplateTile } from './OperatorTemplateTile';

/**
 * "Операторы данных" — шаблоны соглашения об обработке ПД, 1:1 старый
 * Orchid-раздел (OperatorListScreen/OperatorEditScreen, см. докблок
 * OperatorTemplateController в Laravel). Раскрывающиеся плитки вместо
 * таблицы — шаблонов немного, полноценная таблица со скроллом избыточна
 * (тот же выбор, что и у "Каталог", см. TestsCatalogPage.tsx). Создание и
 * редактирование — отдельный экран внутри той же карточки, без поиска и
 * списка остальных шаблонов рядом (в отличие от прежней версии, где
 * редактирование раскрывалось прямо в плитке списка).
 */
export function DataOperatorsPage() {
  const [search, searchInput, setSearchInput] = useDebouncedSearch();
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<OperatorTemplateRow | null>(null);
  /** Раскрыт ровно один шаблон: раскрытие другого схлопывает предыдущий. */
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [deleting, setDeleting] = useState(false);
  const { confirm, dialog } = useConfirmDialog();
  const deleteTooltip = useTooltip<HTMLButtonElement>('Удалить шаблон');
  const reload = () => setReloadToken((t) => t + 1);

  const formOpen = creating || editingTemplate !== null;

  useEffect(() => {
    setPage(1);
  }, [search]);

  // Открытая форма занимает всё место списка — грузить его в это время незачем.
  const {
    data: list,
    error,
    setData,
  } = useAsyncData(
    formOpen ? null : () => fetchOperatorTemplates({ search: search || undefined, page }),
    [search, page, reloadToken, formOpen],
    'Не удалось загрузить список шаблонов.',
  );
  const rows = list?.items ?? null;
  const meta = list?.meta ?? null;

  /**
   * Локально патчим не только целевую строку – setDefault на сервере
   * сбрасывает default у всех остальных. Порядок тоже пересобираем: дефолтный
   * шаблон всегда первый (как и в ответе сервера, см. OperatorTemplateController).
   */
  const patchRow = (updated: OperatorTemplateRow) => {
    setData((prev) => {
      if (!prev) return prev;
      const patched = prev.items.map((r) => {
        if (r.id === updated.id) return updated;
        return updated.default && r.default ? { ...r, default: false } : r;
      });
      return {
        ...prev,
        items: [...patched].sort(
          (a, b) => Number(b.default) - Number(a.default) || a.name.localeCompare(b.name, 'ru'),
        ),
      };
    });
  };

  const closeForm = () => {
    setCreating(false);
    setEditingTemplate(null);
  };

  /** Удаление прямо из режима редактирования — то же подтверждение и тот же вызов, что в меню плитки. */
  const deleteEditing = async () => {
    if (!editingTemplate) return;
    const ok = await confirm(`Удалить шаблон «${editingTemplate.name}»? Это действие необратимо.`, {
      confirmLabel: 'Удалить',
      danger: true,
    });
    if (!ok) return;
    setDeleting(true);
    try {
      await deleteOperatorTemplate(editingTemplate.id);
      closeForm();
      showToast('Шаблон удалён');
      reload();
    } catch {
      showToast('Не удалось удалить шаблон.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <section className="catalog">
      <header className="catalog__header catalog__header--row">
        <div>
          <h1>{creating ? 'Новый шаблон' : editingTemplate ? `Редактирование: ${editingTemplate.name}` : 'Операторы данных'}</h1>
          <p className="catalog__lede">
            {formOpen ? 'Заполните данные шаблона соглашения об обработке персональных данных.' : 'Шаблоны соглашения об обработке персональных данных.'}
          </p>
        </div>
        {!formOpen && (
          <button type="button" className="btn btn--primary" onClick={() => setCreating(true)}>
            <Icon name="plus" size={15} /> Добавить шаблон
          </button>
        )}
        {editingTemplate && (
          <button
            ref={deleteTooltip.ref}
            type="button"
            className="btn btn--icon-danger"
            onClick={deleteEditing}
            disabled={deleting}
            aria-label="Удалить шаблон"
            {...deleteTooltip.handlers}
          >
            <Icon name="trash" size={17} />
            {deleteTooltip.node}
          </button>
        )}
      </header>

      <div className="card">
        {creating && (
          <OperatorTemplateForm
            onCancel={closeForm}
            onSave={async (payload) => {
              const created = await createOperatorTemplate(payload);
              closeForm();
              showToast(`Шаблон «${created.name}» создан`);
              reload();
            }}
          />
        )}

        {editingTemplate && (
          <OperatorTemplateForm
            initial={editingTemplate}
            onCancel={closeForm}
            onSave={async (payload) => {
              const updated = await updateOperatorTemplate(editingTemplate.id, payload);
              closeForm();
              showToast(`Шаблон «${updated.name}» обновлён`);
              reload();
            }}
          />
        )}

        {!formOpen && (
          <>
            <div className="filter-row">
              <SearchBox value={searchInput} onChange={setSearchInput} placeholder="Поиск по названию" />
            </div>

            {error && <p className="field-error">{error}</p>}

            {rows === null ? (
              <div className="panel-loading">
                <Spinner />
              </div>
            ) : rows.length === 0 ? (
              <p className="cell-muted">Шаблоны не найдены.</p>
            ) : (
              <div className="catalog-tiles">
                {rows.map((row) => (
                  <OperatorTemplateTile
                    key={row.id}
                    template={row}
                    expanded={expandedId === row.id}
                    onToggleExpand={() => setExpandedId((id) => (id === row.id ? null : row.id))}
                    onEditRequest={() => setEditingTemplate(row)}
                    onSaved={patchRow}
                    onDeleted={reload}
                  />
                ))}
              </div>
            )}

            {meta && meta.lastPage > 1 && (
              <div className="table-footer">
                <span className="table-footer__status">
                  Страница {meta.currentPage} из {meta.lastPage} · всего {meta.total}
                </span>
                <Pager page={meta.currentPage} lastPage={meta.lastPage} onPageChange={setPage} />
              </div>
            )}
          </>
        )}
      </div>

      {dialog}
    </section>
  );
}
